import { refreshSession } from './api/client.js';
import { startRealtime } from './realtime.js';
import { hydrateTokens } from './api/tokens.js';
import { isNative, platform, startDeepLinks } from './platform.js';
import { getRefreshToken } from './api/tokens.js';
import { navigate, route, setGuard, setNotFound, startRouter, type View } from './router.js';
import { el } from './dom.js';
import { homeView } from './views/home.js';
import { loginView } from './views/login.js';
import { groupsView } from './views/groups.js';
import { groupSearchView } from './views/groupSearch.js';
import { groupView } from './views/group.js';
import { groupManageView } from './views/groupManage.js';
import { eventsView } from './views/events.js';
import { eventView } from './views/event.js';
import { eventNewView } from './views/eventNew.js';
import { eventEditView } from './views/eventEdit.js';
import { calendarView } from './views/calendar.js';
import { leaderboardView } from './views/leaderboard.js';
import { photosView } from './views/photos.js';
import { seriesDetailView, seriesListView, seriesNewView } from './views/series.js';
import { accountView, emailChangeView } from './views/account.js';
import { profileView } from './views/profile.js';
import { groupNotificationsView, notificationsView } from './views/notifications.js';
import { confirmTokenView, unsubscribeView } from './views/tokenPages.js';
import { emailPrefsView } from './views/emailPrefs.js';
import { chatView } from './views/chat.js';
import { tournamentView } from './views/tournament.js';
import { fastingView } from './views/fasting.js';
import { fastingHistoryView } from './views/fastingHistory.js';
import { fastingLearnView } from './views/fastingLearn.js';
import { tournamentBoardView } from './views/tournamentBoard.js';
import { feedView } from './views/feed.js';
import { registerView } from './views/register.js';
import { joinView } from './views/join.js';
import { verifyView } from './views/verify.js';
import { forgotView } from './views/forgot.js';
import { resetView } from './views/reset.js';
import { goodbyeView } from './views/goodbye.js';
import { getAppView } from './views/getApp.js';
import { welcomeView } from './views/welcome.js';
import { privacyView } from './views/privacy.js';
import { termsView } from './views/terms.js';
import { smsConsentView } from './views/smsConsent.js';
import { contactView } from './views/contact.js';
import { allEventsView } from './views/allEvents.js';
import { adminView } from './views/admin.js';
import { adminGroupsView } from './views/adminGroups.js';
import { adminUsersView } from './views/adminUsers.js';
import { mountChrome } from './chrome.js';
import { applyViewMode } from './viewMode.js';
import { contactsView } from './views/contacts.js';
import { socialtieHomeView } from './views/socialtieHome.js';
import { familyTreeView } from './views/familyTree.js';
import { brand, brandLogo, brandName, isSocialTie } from './brand.js';
import { getRefreshToken as hasSession } from './api/tokens.js';

/**
 * `/` means something different depending on which name was typed.
 *
 * On socialtie.com the root is the address book for a signed-in visitor and the
 * public front door for everyone else. On mysocialcal.com it stays the events
 * dashboard it has always been, and a signed-out visitor is bounced to /login by
 * the guard in boot() exactly as before.
 *
 * The session test is `getRefreshToken()`, not a /me call: this runs after
 * hydrateTokens() and refreshSession() in boot(), so the token is already
 * settled, and asking the server would put a network round trip in front of the
 * landing page for a visitor who has no account to look up.
 */
const rootView: View = (outlet, params) => {
  if (!isSocialTie()) return homeView(outlet, params);
  return hasSession() ? contactsView(outlet, params) : socialtieHomeView(outlet, params);
};

route('/', rootView);
route('/family-tree', familyTreeView);
route('/login', loginView);
route('/groups', groupsView);
// ⚠ Before any '/groups/:groupId' pattern, for the same reason the server
// registers /groups/search before /groups/:groupId: a param route would
// otherwise match 'find' as a group id.
route('/groups/find', groupSearchView);
route('/events', allEventsView);
// Same view as the group calendar: no :groupId means every group at once.
route('/calendar', calendarView);
route('/admin', adminView);
route('/admin/groups', adminGroupsView);
route('/admin/users', adminUsersView);
// The group's home is its FEED, not its roster. The roster is /members.
// See docs/plan-feed-as-home.md.
route('/groups/:groupId', feedView);
route('/groups/:groupId/members', groupView);
route('/groups/:groupId/manage', groupManageView);
route('/groups/:groupId/events', eventsView);
// More specific than /events/:eventId in segment count, so no ordering worry:
// the router matches on segment count first.
route('/groups/:groupId/events/new', eventNewView);
route('/groups/:groupId/calendar', calendarView);
route('/groups/:groupId/leaderboard', leaderboardView);
route('/groups/:groupId/photos', photosView);
route('/users/:userId', profileView);
route('/account', accountView);
// The landing page for the emailed confirmation link. Signed-in only, like the
// account page it belongs to -- the token proves the ADDRESS, never the session.
route('/email-change', emailChangeView);
route('/contacts', contactsView);
route('/groups/:groupId/series', seriesListView);
route('/groups/:groupId/series/new', seriesNewView);
route('/series/:seriesId', seriesDetailView);
route('/settings/notifications', notificationsView);
route('/settings/notifications/groups/:groupId', groupNotificationsView);
route('/confirm', confirmTokenView);
route('/unsubscribe', unsubscribeView);
route('/email-preferences', emailPrefsView);
route('/events/:eventId', eventView);
route('/events/:eventId/edit', eventEditView);
route('/events/:eventId/chat', chatView);
route('/groups/:groupId/fasting', fastingView);
route('/groups/:groupId/fasting/history', fastingHistoryView);
// NOT group-scoped: none of the education content is group-specific.
route('/fasting/learn', fastingLearnView);
route('/events/:eventId/tournament', tournamentView);
// The projector view. A longer path with one more segment, and the router
// matches on segment COUNT first, so it cannot be swallowed by the line above.
route('/events/:eventId/tournament/board', tournamentBoardView);
// Kept as an alias of the group home, not deleted: /groups/:id/feed is in
// notification emails already sent, in browser history, and in anything a member
// bookmarked while the feed was a tab.
route('/groups/:groupId/feed', feedView);
route('/register', registerView);
route('/join/:code', joinView);
route('/verify', verifyView);
route('/forgot', forgotView);
route('/reset', resetView);
route('/goodbye', goodbyeView);
route('/privacy', privacyView);
route('/terms', termsView);
route('/sms-consent', smsConsentView);
route('/contact', contactView);
route('/app', getAppView);
route('/welcome', welcomeView);

/**
 * Routes reachable without a session. The verify and reset links arrive by
 * email, so bouncing an unauthenticated visitor to /login would break them.
 */
const PUBLIC_PATHS = new Set([
  '/login',
  '/register',
  '/verify',
  '/forgot',
  '/reset',
  // Signed one-click links. Bouncing these to /login would defeat the entire
  // point: a day-of "still coming?" tap has to work from a phone with no session.
  '/confirm',
  '/unsubscribe',
  '/email-preferences',
  '/privacy',
  '/terms',
  // The A2P opt-in proof: a carrier reviewer opens it with no account.
  '/sms-consent',
  '/contact',
  // The address people are given for the iPhone app. Most of them have no
  // account yet, so bouncing them to /login would hide the download link.
  '/app',
  // Where a just-deleted account lands. It is signed out BY DEFINITION -- the
  // tokens are dropped before the response is handled -- so bouncing it to
  // /login would hide the one page that says the deletion worked.
  '/goodbye',
]);

setNotFound((outlet) => {
  outlet.append(
    el('main', { class: 'page' }, [
      el('h1', {}, 'Not found'),
      el('a', { href: '/' }, 'Back to home'),
    ]),
  );
});

async function boot(): Promise<void> {
  const mount = document.getElementById('app');
  if (!mount) throw new Error('#app missing from the shell');

  // Load the persisted refresh token into memory FIRST. On native it lives in the
  // platform keystore behind an async API, so `getRefreshToken()` returns null
  // until this resolves — calling it earlier would look exactly like being signed
  // out and bounce a returning user to the login screen on every cold start.
  await hydrateTokens();

  // A reload loses the in-memory access token. Recover it from the refresh
  // token before the first view renders, so /me does not bounce to /login.
  if (getRefreshToken()) await refreshSession();

  // Deep links route through the SPA router rather than reloading: the shell has
  // no server to ask, and a reload would drop the WebSocket.
  void startDeepLinks((path) => navigate(path));

  // The shell ships with MySocialCal's title and icon, which are wrong on the
  // other name. Set from the brand before the first paint so the tab never shows
  // the other product's name.
  document.title = brandName();
  // Published as an attribute rather than a class on the logo itself so any part
  // of the app can style per brand without threading a flag through to it.
  document.documentElement.dataset.brand = brand();
  if (isSocialTie()) {
    for (const rel of ['icon', 'apple-touch-icon']) {
      const link = document.querySelector(`link[rel="${rel}"]`);
      if (link) link.setAttribute('href', brandLogo());
    }
  }

  if (isNative()) document.documentElement.dataset.platform = platform();

  // Before anything renders, so a remembered "Desktop view" never flashes the
  // phone layout first.
  applyViewMode();

  // Open the socket before the first view renders, so a chat view's subscribe
  // does not wait on a handshake. It is a session-long singleton and survives
  // every navigation — the whole reason this is an SPA.
  startRealtime();

  // Installed BEFORE startRouter's first render, so the cold-load bounce and
  // every later in-app navigation go through the same rule. It used to run once
  // here at the end of boot(), which meant a signed-out visitor who clicked any
  // in-app link to a private path got that view rendering against 401s instead
  // of the login screen.
  setGuard((path) => {
    // socialtie.com's root is a public marketing page, so it must NOT be bounced
    // to /login. On mysocialcal.com the root stays private and the bounce stands.
    const isPublic = PUBLIC_PATHS.has(path) || path.startsWith('/join/') || (isSocialTie() && path === '/');
    return !getRefreshToken() && !isPublic ? '/login' : null;
  });

  // Chrome first: it takes over #app and hands back the element the router owns,
  // so the nav is a SIBLING of the outlet rather than inside it. The router wipes
  // its outlet on every navigation, and the nav must survive that.
  startRouter(mountChrome(mount));
}

void boot();
